import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { create } from 'zustand';

import { type RegistrationCredential, webauthnBeginRegistration, webauthnFinishRegistration } from '@column/api';
import { Button, Icon, Input, Modal, ModalSize, Tooltip } from '@column/column-ui-kit';

import { useDialog } from '~/components/Dialog/Provider';
import { PasskeyIcon } from '~/elements';
import { createPublicKeyCredential, getDefaultPasskeyName, isWebAuthnSupported } from '~/lib/webauthn';
import { FactorTypeResponse, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Box, Flex, Inner, Text } from '~/styles';
import { formatDateTime } from '~/util';

const PasskeyAddedTimestamp: React.FC<{ date: Date }> = ({ date }) => (
  <Text size={12} weight="normal" color="light">
    Created {formatDateTime(date, { timeZoneFormat: 'local' })}
  </Text>
);

const Container = styled.div`
  container-type: inline-size;
  container-name: passkeys;
`;

const Wrapper = styled.div`
  display: grid;
  grid-column-gap: 24px;
  grid-row-gap: 8px;
  align-items: start;
  grid-template-columns: auto;

  @container passkeys (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    grid-template-columns: minmax(180px, 25%) 1fr;
    grid-row-gap: 0;
  }

  @container passkeys (min-width: ${({ theme }) => theme.containerBreakpoints.wide}) {
    grid-template-columns: minmax(180px, 25%) auto 25%;
  }
`;

const Label = styled.div`
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  color: ${({ theme }) => theme.foreground};

  @container passkeys (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    margin-top: 16px;
  }
`;

// Fade + scale used when the center-column content swaps (card <-> empty state).
const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const cardExit = keyframes`
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.96);
  }
`;

// Mirrors the ui field-group / ui-kit OptionGroup container shadow: the secondary
// halo border + a 1px drop, without the larger soft layer the plain Card shadow adds.
const PasskeyGroup = styled.div<{ $exiting?: boolean }>`
  background-color: ${({ theme }) => theme.background};
  border-radius: ${({ theme }) => theme.style.borderRadiusM};
  overflow: hidden;
  animation: ${cardEnter} 0.22s ease;
  box-shadow:
    ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
      ${({ theme }) => theme.style.buttonSecondaryBorderWidth} ${({ theme }) => theme.style.buttonSecondaryBorderColor},
    0 1px 1px rgba(0, 0, 0, 0.1),
    0 1px 4px 0 transparent inset;

  ${({ $exiting }) =>
    $exiting &&
    css`
      animation: ${cardExit} 0.2s ease forwards;
      pointer-events: none;
    `}
`;

const DeviceList = styled.ul`
  display: flex;
  flex-direction: column;
  padding: 0;
  margin: 0;
  gap: 0;
`;

const collapse = keyframes`
  from {
    opacity: 1;
    max-height: 80px;
  }
  to {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const expand = keyframes`
  from {
    opacity: 0;
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
  to {
    opacity: 1;
    max-height: 80px;
    padding-top: 12px;
    padding-bottom: 12px;
  }
`;

const DeviceRow = styled.li<{ $exiting?: boolean; $entering?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  overflow: hidden;
  border-top: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

  &:first-child {
    border-top: none;
  }

  ${({ $entering }) =>
    $entering &&
    css`
      animation: ${expand} 0.24s ease;
    `}

  ${({ $exiting }) =>
    $exiting &&
    css`
      animation: ${collapse} 0.24s ease forwards;
      border-top-color: transparent;
      pointer-events: none;
    `}
`;

const DeviceInfo = styled.div`
  display: grid;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

// No border, transparent by default; a light-red wash with a red icon on hover.
const DeleteButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  margin: 0;
  border: none;
  outline: none;
  border-radius: ${({ theme }) => theme.style.buttonSmallBorderRadius};
  background-color: transparent;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  color: #a9acb6;
  --icon-size: 20px;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.danger.blendToBackground(75)};
    color: ${({ theme }) => theme.danger.background};
  }

  &:focus-visible {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.danger.blendToBackground(250)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const NamePasskeyFields = styled.div`
  display: grid;
  grid-gap: 16px;
  padding-bottom: var(--page-margin);
`;

const NamePasskeyActions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
`;

// Light-gray empty state shown in place of the devices card when no passkeys exist.
// Below the container breakpoint the button bumps below the text and goes full width.
const EmptyCard = styled(Box)`
  animation: ${cardEnter} 0.22s ease;
`;

const EmptyPrompt = styled.div`
  container-type: inline-size;
`;

const EmptyRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @container (max-width: 420px) {
    flex-direction: column;
    align-items: stretch;

    .passkey-empty-action {
      width: 100%;
    }
  }
`;

const EmptyMain = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: auto;
  min-width: 0;
`;

const EmptyText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: auto;
  min-width: 0;
`;

const StyledPasskeyIcon = styled(PasskeyIcon)`
  --icon-size: 24px;
  color: #a9acb6;
  flex-shrink: 0;
  align-self: flex-start;
`;

interface PendingRegistration {
  challengeId: string;
  credential: RegistrationCredential;
}

// Shared so the header button (a separate subtree) can hide the moment the last passkey is being
// removed, instead of waiting for the refetch to empty the session store.
const useRemovingLastPasskey = create<{ removingLast: boolean; setRemovingLast: (removingLast: boolean) => void }>(
  (set) => ({
    removingLast: false,
    setRemovingLast: (removingLast) => set({ removingLast }),
  })
);

export const AddPasskeyButton: React.FC<{ className?: string; hideWhenEmpty?: boolean; label?: string }> = ({
  className,
  hideWhenEmpty,
  label = 'Create Passkey',
}) => {
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();
  const hasPasskeys = useSessionStore((state) =>
    (state.currentUser?.factors ?? []).some((factor) => factor.factorType === FactorTypeResponse.WEBAUTHN)
  );
  const removingLast = useRemovingLastPasskey((state) => state.removingLast);
  const firstName = useSessionStore((state) => state.currentUser?.firstName ?? '');
  const refetchCurrentUser = useSessionStore((state) => state.refetchCurrentUser);
  const isSupported = isWebAuthnSupported();
  const [isOpen, setIsOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [pendingRegistration, setPendingRegistration] = useState<PendingRegistration>();

  const beginPasskeyRegistrationMutation = useMutation({
    mutationFn: async (): Promise<PendingRegistration> => {
      const { data: registration } = await webauthnBeginRegistration({ throwOnError: true });
      const credential = await createPublicKeyCredential(registration.options);

      return {
        challengeId: registration.challenge_id,
        credential,
      };
    },
    onSuccess: (registration) => {
      setPendingRegistration(registration);
      setDisplayName(getDefaultPasskeyName(firstName));
      setIsOpen(true);
    },
    onError: (error: unknown) => {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        return;
      }
      // The browser throws InvalidStateError when the authenticator is already in excludeCredentials.
      if (error instanceof DOMException && error.name === 'InvalidStateError') {
        addDangerNotification({ content: 'A passkey has already been registered to this device' });
        return;
      }
      const content = error instanceof Error ? error.message : 'Failed to add passkey. Please try again.';
      addDangerNotification({ content });
    },
  });

  const finishPasskeyRegistrationMutation = useMutation({
    mutationFn: async ({ name, registration }: { name: string; registration: PendingRegistration }) => {
      await webauthnFinishRegistration({
        body: {
          challenge_id: registration.challengeId,
          credential: registration.credential,
          display_name: name,
        },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      addSuccessNotification({ content: 'Passkey added' });
      void refetchCurrentUser();
      setIsOpen(false);
      setDisplayName('');
      setPendingRegistration(undefined);
    },
    onError: (error: unknown) => {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        return;
      }
      const content = error instanceof Error ? error.message : 'Failed to add passkey. Please try again.';
      addDangerNotification({ content });
    },
  });

  const createButton = (
    <Button
      type="button"
      size="small"
      variant="secondary"
      icon={<Icon.Plus />}
      className={className}
      isDisabled={!isSupported || beginPasskeyRegistrationMutation.isPending}
      isLoading={beginPasskeyRegistrationMutation.isPending}
      onClick={() => beginPasskeyRegistrationMutation.mutate()}
    >
      {label}
    </Button>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
          setDisplayName('');
          setPendingRegistration(undefined);
        }}
        size={ModalSize.Small}
      >
        <Modal.IconCircle type="primary">
          <Icon.Key />
        </Modal.IconCircle>

        <form
          onSubmit={(event) => {
            event.stopPropagation();
            event.preventDefault();
            const name = displayName.trim();
            if (name && pendingRegistration) {
              finishPasskeyRegistrationMutation.mutate({ name, registration: pendingRegistration });
            }
          }}
        >
          <Modal.Content>
            <Modal.Headline fullWidth align="center">
              Name your passkey
            </Modal.Headline>
            <NamePasskeyFields>
              <Modal.Paragraph fullWidth align="center">
                Use a name that helps you recognize this key
              </Modal.Paragraph>
              <Input value={displayName} onChange={setDisplayName} placeholder="e.g. Work laptop" maxLength={127} />

              <NamePasskeyActions>
                <Button
                  type="button"
                  size="small"
                  variant="secondary"
                  onClick={() => {
                    setIsOpen(false);
                    setDisplayName('');
                    setPendingRegistration(undefined);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="small"
                  isDisabled={!displayName.trim() || !pendingRegistration}
                  isLoading={finishPasskeyRegistrationMutation.isPending}
                >
                  Add passkey
                </Button>
              </NamePasskeyActions>
            </NamePasskeyFields>
          </Modal.Content>
        </form>
      </Modal>

      {(!hideWhenEmpty || (hasPasskeys && !removingLast)) &&
        (isSupported ? (
          createButton
        ) : (
          <Tooltip content="Passkeys are not supported by this browser">{createButton}</Tooltip>
        ))}
    </>
  );
};

export const SectionPasskeys: React.FC = () => {
  const { openAlert } = useDialog();
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();
  const currentUser = useSessionStore((state) => state.currentUser);
  const refetchCurrentUser = useSessionStore((state) => state.refetchCurrentUser);
  const passkeys = currentUser?.factors.filter((factor) => factor.factorType === FactorTypeResponse.WEBAUTHN) ?? [];
  const [exitingId, setExitingId] = useState<string | null>(null);
  // When the last passkey is removed the whole card fades/scales out and the empty state fades in.
  // Shared so the header button hides immediately (see useRemovingLastPasskey).
  const cardExiting = useRemovingLastPasskey((state) => state.removingLast);
  const setCardExiting = useRemovingLastPasskey((state) => state.setRemovingLast);
  // Flipped as soon as the card finishes animating out so the empty state fades in immediately,
  // rather than waiting for the refetch to empty the list.
  const [optimisticEmpty, setOptimisticEmpty] = useState(false);

  // Guard against a stale shared flag if we leave mid-animation.
  useEffect(() => () => setCardExiting(false), [setCardExiting]);

  // Track which rows have already been rendered so only freshly-added passkeys animate in
  // (not the ones present on first render), and skip the row animation when the whole card is
  // mounting from the empty state — the card's own fade covers that transition.
  const seenIdsRef = useRef<Set<string>>(new Set(passkeys.map((passkey) => passkey.factorId)));
  const prevCountRef = useRef(passkeys.length);

  useEffect(() => {
    passkeys.forEach((passkey) => seenIdsRef.current.add(passkey.factorId));
    prevCountRef.current = passkeys.length;
  });

  // Once the refetch has actually emptied the list, drop the transient exit flags.
  useEffect(() => {
    if (passkeys.length === 0 && (cardExiting || optimisticEmpty)) {
      setCardExiting(false);
      setOptimisticEmpty(false);
    }
  }, [passkeys.length, cardExiting, optimisticEmpty, setCardExiting]);

  const isEntering = (factorId: string) => prevCountRef.current > 0 && !seenIdsRef.current.has(factorId);

  const deletePasskeyMutation = useMutation({
    mutationFn: ({ factorId }: { factorId: string }) => UserRepository.deleteFactor({ factorId }),
    onSuccess: () => {
      addSuccessNotification({ content: 'Passkey deleted' });
      // Leave the exit flags set so the row/card stays out while the refetch removes it.
      void refetchCurrentUser();
    },
    onError: (error: unknown) => {
      setExitingId(null);
      setCardExiting(false);
      setOptimisticEmpty(false);
      const content = error instanceof Error ? error.message : 'Failed to delete passkey. Please try again.';
      addDangerNotification({ content });
    },
  });

  const handleDelete = (passkey: { factorId: string; displayName: string }) => {
    openAlert({
      headline: 'Delete passkey?',
      content: `Are you sure you want to delete ${passkey.displayName}? You will no longer be able to use it to sign in.`,
      icon: 'Trash',
      confirmButton: {
        label: 'Delete',
        variant: 'danger',
      },
      onSuccess: () => {
        if (passkeys.length === 1) {
          setCardExiting(true);
        } else {
          setExitingId(passkey.factorId);
        }
        deletePasskeyMutation.mutate({ factorId: passkey.factorId });
      },
    });
  };

  return (
    <Container>
      <Wrapper>
        <Label>Your Passkeys</Label>
        {passkeys.length === 0 || optimisticEmpty ? (
          <EmptyCard variant="secondary">
            <Inner py={12} px={16}>
              <EmptyPrompt>
                <EmptyRow>
                  <EmptyMain>
                    <StyledPasskeyIcon />
                    <EmptyText>
                      <Text size={14} weight="medium" color="foreground">
                        Create Passkey
                      </Text>
                      <Text size={12} weight="normal" color="light">
                        Passkeys are more secure than passwords and the strongest protection against attacks like
                        phishing.
                      </Text>
                    </EmptyText>
                  </EmptyMain>
                  <AddPasskeyButton className="passkey-empty-action" label="Create" />
                </EmptyRow>
              </EmptyPrompt>
            </Inner>
          </EmptyCard>
        ) : (
          <PasskeyGroup
            $exiting={cardExiting}
            onAnimationEnd={(event) => {
              if (cardExiting && event.target === event.currentTarget) {
                setOptimisticEmpty(true);
              }
            }}
          >
            <DeviceList>
              {passkeys.map((passkey) => (
                <DeviceRow
                  key={passkey.factorId}
                  $exiting={exitingId === passkey.factorId}
                  $entering={isEntering(passkey.factorId)}
                >
                  <DeviceInfo>
                    <Text size={14} weight="medium" color="foreground">
                      {passkey.displayName}
                    </Text>
                    {passkey.createdAt && <PasskeyAddedTimestamp date={passkey.createdAt} />}
                  </DeviceInfo>
                  <Flex alignItems="center">
                    <DeleteButton
                      type="button"
                      aria-label={`Delete ${passkey.displayName}`}
                      disabled={deletePasskeyMutation.isPending}
                      onClick={() => handleDelete(passkey)}
                    >
                      <Icon.Trash />
                    </DeleteButton>
                  </Flex>
                </DeviceRow>
              ))}
            </DeviceList>
          </PasskeyGroup>
        )}
      </Wrapper>
    </Container>
  );
};
